/** What's next (backend plan 2026-10-29-dashboard-whats-next-setup): the setup steps the client completes itself
 *  once easy onboarding has set the account up, grouped (the app, the brand, contacts and legal pages, the guest
 *  experience, the team). Each step is a row with its state and, on demand, its inline form, its image upload or a
 *  link to the dashboard page where it is done; an optional step can be hidden and shown again.
 *
 *  The steps and their state come from `GET setup_steps/` (contract apiv2/dashboard_setup_steps.openapi.yaml): the
 *  state is the account's data, so a value entered in Settings completes a step too. A save, upload or hide answers
 *  the step, which replaces its row. A user without `tour_operator.write` reads the list only. The Onboarding page's
 *  Ready screen shows it as a section; the dashboard home shows it as the "Finish your setup" card (`card`), which
 *  renders nothing while it loads, on a refusal, or once every step is done or hidden. */
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import {
    Box, Button, CircularProgress, Collapse, FormControlLabel, Stack, Switch, TextField, Typography,
} from '@mui/material';
import * as React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { imageURL } from '../components/images';
import { ContextStore } from '../store/contextStore';
import { OnboardingApiError } from './api';
import { ErrorLine, LaneStateIcon, Section } from './components';
import { ACCEPTED_TYPES, MAX_UPLOAD_BYTES } from './LogoCard';
import { useSetupStepsApi } from './whatsNextApi';
import type { SetupStepsApi } from './whatsNextApi';
import { SETUP_ERROR_CODES, SETUP_GROUPS, SETUP_STATES, STATE_ICONS, STEP_PAGES, UPLOAD_HINTS } from './whatsNextLabels';
import type { SetupStep, SetupStepField, SetupStepValues } from './whatsNextTypes';

/** The contract's group order; a group this dashboard does not know goes last. */
const GROUP_ORDER = Object.keys(SETUP_GROUPS);

/** Not done and not hidden: what is left to do. */
export const isLeft = (s: SetupStep) => !s.dismissed && s.state !== 'done';

interface Props {
    title?: string;
    intro?: React.ReactNode;
    testId?: string;
    /** Steps shown elsewhere on the page (the Onboarding page's logo card). */
    hideKeys?: string[];
    /** The home card: nothing while loading, on a refusal, or once nothing is left to do. */
    card?: boolean;
    /** A refusal of the list (the home card remembers `not_easy`). */
    onRefused?: (error: unknown) => void;
    /** Injected in tests; the session's API otherwise. */
    api?: SetupStepsApi;
}

export default function WhatsNext({ title = "What's next", intro, testId = 'whats-next', hideKeys = [], card = false, onRefused, api: injected }: Props) {
    const sessionApi = useSetupStepsApi();
    const api = injected || sessionApi;
    const { hasPerm } = React.useContext(ContextStore) as any;
    const canWrite = !!hasPerm?.('tour_operator', 'write');
    const [steps, setSteps] = React.useState<SetupStep[] | null>(null);
    const [error, setError] = React.useState<unknown>(null);
    const [showHidden, setShowHidden] = React.useState(false);
    const refusedRef = React.useRef(onRefused);
    refusedRef.current = onRefused;

    React.useEffect(() => {
        const ctrl = new AbortController();
        api.list(ctrl.signal).then(
            (list) => {
                if (!ctrl.signal.aborted) {
                    setSteps(list.steps);
                }
            },
            (e) => {
                if (!ctrl.signal.aborted && !(e && e.name === 'AbortError')) {
                    setError(e);
                    refusedRef.current?.(e);
                }
            },
        );
        return () => ctrl.abort();
    }, [api]);

    const replace = React.useCallback((next: SetupStep) => {
        setSteps((prev) => (prev || []).map((s) => (s.key === next.key ? next : s)));
    }, []);

    const shown = (steps || []).filter((s) => !hideKeys.includes(s.key));
    const active = shown.filter((s) => !s.dismissed);
    const hidden = shown.filter((s) => s.dismissed);
    const left = active.filter(isLeft).length;

    if (card && (!steps || left === 0)) {
        return null;
    }
    if (!steps) {
        return (
            <Section title={title} testId={testId}>
                {error ? <ErrorLine error={error} own={SETUP_ERROR_CODES} testId={`${testId}-error`} /> : <CircularProgress size={20} />}
            </Section>
        );
    }
    if (!shown.length) {
        return null;
    }
    const groups = Array.from(new Set(active.map((s) => s.group)))
        .sort((a, b) => (GROUP_ORDER.indexOf(a) + 1 || 99) - (GROUP_ORDER.indexOf(b) + 1 || 99));

    return (
        <Section
            title={title}
            testId={testId}
            action={(
                <Typography variant="caption" color="text.secondary" data-testid={`${testId}-count`}>
                    {left === 0 ? 'All done' : `${left} of ${active.length} left`}
                </Typography>
            )}
        >
            {intro && <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '68ch', mb: 1 }}>{intro}</Typography>}
            {groups.map((group) => (
                <Box key={group} data-testid={`${testId}-group-${group}`} sx={{ mt: 1 }}>
                    <Typography variant="overline" color="text.secondary" component="h3" sx={{ lineHeight: 2 }}>
                        {SETUP_GROUPS[group] || group}
                    </Typography>
                    {active.filter((s) => s.group === group).map((step) => (
                        <StepRow key={step.key} step={step} api={api} canWrite={canWrite} onChange={replace} />
                    ))}
                </Box>
            ))}
            {hidden.length > 0 && (
                <Box sx={{ mt: 1 }}>
                    <Button size="small" color="inherit" onClick={() => setShowHidden((v) => !v)} aria-expanded={showHidden}>
                        {showHidden ? 'Hide the hidden steps' : `Hidden steps (${hidden.length})`}
                    </Button>
                    <Collapse in={showHidden} unmountOnExit>
                        {hidden.map((step) => (
                            <StepRow key={step.key} step={step} api={api} canWrite={canWrite} onChange={replace} />
                        ))}
                    </Collapse>
                </Box>
            )}
        </Section>
    );
}

function StepRow({ step, api, canWrite, onChange }: {
    step: SetupStep;
    api: SetupStepsApi;
    canWrite: boolean;
    onChange: (step: SetupStep) => void;
}) {
    const [open, setOpen] = React.useState(false);
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState<unknown>(null);
    const page = STEP_PAGES[step.key];
    const editable = canWrite && !step.dismissed && (step.editor === 'fields' || step.editor === 'upload');
    // Hiding or showing moves the row to the other list: this one unmounts with the answer.
    const mounted = React.useRef(true);
    React.useEffect(() => () => { mounted.current = false; }, []);

    const setHidden = async (dismissed: boolean) => {
        setBusy(true);
        setError(null);
        try {
            onChange(await api.dismiss(step.key, dismissed));
        } catch (e) {
            if (mounted.current) {
                setError(e);
            }
        } finally {
            if (mounted.current) {
                setBusy(false);
            }
        }
    };
    const changed = (next: SetupStep) => {
        onChange(next);
        if (next.state === 'done') {
            setOpen(false);
        }
    };

    return (
        <Box
            data-testid={`step-${step.key}`}
            sx={{ display: 'grid', gridTemplateColumns: '22px minmax(0,1fr)', columnGap: 1, alignItems: 'start', py: 1, borderTop: 1, borderColor: 'divider' }}
        >
            <Box sx={{ pt: '2px' }}><LaneStateIcon state={STATE_ICONS[step.state] || 'pending'} /></Box>
            <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" useFlexGap>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{step.title}</Typography>
                    <Typography
                        variant="caption"
                        color={step.state === 'attention' ? 'warning.dark' : 'text.secondary'}
                        data-testid={`step-${step.key}-state`}
                    >
                        {step.dismissed ? 'Hidden' : SETUP_STATES[step.state] || step.state}
                    </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '68ch' }}>{step.why}</Typography>
                {step.summary && (
                    <Typography
                        variant="body2"
                        color={step.state === 'attention' ? 'warning.dark' : 'text.primary'}
                        sx={{ maxWidth: '68ch', mt: 0.25 }}
                        data-testid={`step-${step.key}-summary`}
                    >
                        {step.summary}
                    </Typography>
                )}
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                    {editable && (
                        <Button size="small" onClick={() => setOpen((v) => !v)} aria-expanded={open} disabled={busy}>
                            {open ? 'Close' : step.state === 'done' ? 'Change' : 'Set up'}
                        </Button>
                    )}
                    {page && !step.dismissed && (
                        <Button size="small" component={RouterLink} to={page.path}>{page.label}</Button>
                    )}
                    {canWrite && step.optional && step.dismissed && (
                        <Button size="small" disabled={busy} onClick={() => setHidden(false)}>Show again</Button>
                    )}
                    {canWrite && step.optional && !step.dismissed && step.state !== 'done' && (
                        <Button size="small" color="inherit" disabled={busy} onClick={() => setHidden(true)}>Hide</Button>
                    )}
                </Stack>
                <ErrorLine error={error} own={SETUP_ERROR_CODES} testId={`step-${step.key}-error`} />
                {editable && (
                    <Collapse in={open} unmountOnExit>
                        {step.editor === 'fields' ? (
                            <StepFields step={step} api={api} onSaved={changed} />
                        ) : (
                            <StepUpload step={step} api={api} onUploaded={changed} />
                        )}
                    </Collapse>
                )}
            </Box>
        </Box>
    );
}

type Draft = Record<string, string | boolean>;

/** The form's starting values. A secret starts empty, never holding the mask the API reads it as: it is sent only
 *  when the user typed one, and an untouched one keeps the stored value. */
const draftOf = (fields: SetupStepField[]): Draft => Object.fromEntries(
    fields.map((f) => [
        f.key,
        f.input === 'bool' ? f.value === true : f.input !== 'secret' && typeof f.value === 'string' ? f.value : '',
    ]),
);

/** The inline form: only the values that changed are sent (a secret only when typed). */
function StepFields({ step, api, onSaved }: { step: SetupStep; api: SetupStepsApi; onSaved: (step: SetupStep) => void }) {
    const initial = React.useMemo(() => draftOf(step.fields), [step.fields]);
    const [draft, setDraft] = React.useState<Draft>(initial);
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState<unknown>(null);
    React.useEffect(() => setDraft(initial), [initial]);

    const fieldErrors: Record<string, string> = (error instanceof OnboardingApiError && error.body && typeof error.body.errors === 'object' && error.body.errors) || {};
    const changes: SetupStepValues = {};
    Object.entries(draft).forEach(([key, value]) => {
        if (value !== initial[key]) {
            changes[key] = value;
        }
    });

    const save = async (ev: React.FormEvent) => {
        ev.preventDefault();
        setBusy(true);
        setError(null);
        try {
            onSaved(await api.save(step.key, changes));
        } catch (e) {
            setError(e);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Stack component="form" spacing={1.5} sx={{ mt: 1, mb: 0.5, maxWidth: 520 }} onSubmit={save} data-testid={`step-${step.key}-form`} noValidate>
            {step.fields.map((f) => (
                <FieldControl
                    key={f.key}
                    field={f}
                    value={draft[f.key]}
                    error={fieldErrors[f.key]}
                    disabled={busy}
                    onChange={(value) => setDraft((d) => ({ ...d, [f.key]: value }))}
                />
            ))}
            <ErrorLine error={Object.keys(fieldErrors).length ? null : error} own={SETUP_ERROR_CODES} testId={`step-${step.key}-save-error`} />
            <Box>
                <Button
                    type="submit"
                    variant="contained"
                    size="small"
                    disabled={busy || Object.keys(changes).length === 0}
                    startIcon={busy ? <CircularProgress size={14} color="inherit" /> : undefined}
                >
                    Save
                </Button>
            </Box>
        </Stack>
    );
}

const HEX = /^#[0-9a-fA-F]{6}$/;

function FieldControl({ field, value, error, disabled, onChange }: {
    field: SetupStepField;
    value: string | boolean;
    error?: string;
    disabled: boolean;
    onChange: (value: string | boolean) => void;
}) {
    if (field.input === 'bool') {
        return (
            <FormControlLabel
                control={<Switch checked={value === true} disabled={disabled} onChange={(ev) => onChange(ev.target.checked)} />}
                label={field.label}
            />
        );
    }
    const text = typeof value === 'string' ? value : '';
    const helper = error || field.help;
    const placeholder = field.input === 'url' ? 'https://'
        : field.input === 'color' ? '#1a73e8'
            : field.input === 'secret' && field.set ? 'Saved — type to replace'
                : undefined;
    const input = (
        <TextField
            label={field.label}
            value={text}
            onChange={(ev) => onChange(ev.target.value)}
            disabled={disabled}
            size="small"
            fullWidth
            error={!!error}
            helperText={helper || undefined}
            type={field.input === 'secret' ? 'password' : field.input === 'url' ? 'url' : field.input === 'email' ? 'email' : 'text'}
            placeholder={placeholder}
            InputLabelProps={placeholder ? { shrink: true } : undefined}
            autoComplete={field.input === 'secret' ? 'new-password' : undefined}
            inputProps={{ 'data-testid': `field-${field.key}` }}
        />
    );
    if (field.input !== 'color') {
        return input;
    }
    return (
        <Stack direction="row" spacing={1} alignItems="flex-start">
            {input}
            <Box
                component="input"
                type="color"
                aria-label={`Pick the ${field.label.toLowerCase()}`}
                value={HEX.test(text) ? text.toLowerCase() : '#000000'}
                disabled={disabled}
                onChange={(ev: React.ChangeEvent<HTMLInputElement>) => onChange(ev.target.value)}
                sx={{ width: 40, height: 40, p: 0, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper', flex: 'none' }}
            />
        </Stack>
    );
}

/** An image step: the image in place (when there is one to show), what to upload, and the upload. A new logo
 *  becomes the session's logo at once (the sidebar follows), as on the Onboarding page's logo card. */
function StepUpload({ step, api, onUploaded }: { step: SetupStep; api: SetupStepsApi; onUploaded: (step: SetupStep) => void }) {
    const { settings, updateTourOperator } = React.useContext(ContextStore) as any;
    const input = React.useRef<HTMLInputElement>(null);
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState<unknown>(null);
    const upload = step.upload;
    if (!upload) {
        return null;
    }
    const current = upload.image ? imageURL(settings?.images?.base, upload.image, 'x192') : null;
    const size = upload.min_width && upload.min_height ? ` At least ${upload.min_width} × ${upload.min_height} px.` : '';

    const onFile = async (ev: React.ChangeEvent<HTMLInputElement>) => {
        const file = ev.target.files && ev.target.files[0];
        ev.target.value = '';
        if (!file) {
            return;
        }
        if (file.size > MAX_UPLOAD_BYTES) {
            setError(new OnboardingApiError(413, { code: 'upload_too_large' }));
            return;
        }
        if (file.type && !ACCEPTED_TYPES.includes(file.type)) {
            setError(new OnboardingApiError(400, { code: 'image_type' }));
            return;
        }
        setBusy(true);
        setError(null);
        try {
            const next = await api.upload(step.key, file);
            if (upload.kind === 'logo' && next.upload && next.upload.image) {
                updateTourOperator?.((top: any) => ({ ...top, logo: next.upload!.image }));
            }
            onUploaded(next);
        } catch (e) {
            setError(e);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Stack spacing={1} sx={{ mt: 1, mb: 0.5 }}>
            {current && (
                <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1, display: 'flex', justifyContent: 'center', bgcolor: 'grey.50', maxWidth: 240 }}>
                    <img src={current} alt="" style={{ maxHeight: 64, maxWidth: '100%', objectFit: 'contain' }} data-testid={`step-${step.key}-image`} />
                </Box>
            )}
            <Typography variant="caption" color="text.secondary">{`${UPLOAD_HINTS[upload.kind] || 'JPG, PNG, GIF or WebP, up to 15 MB.'}${size}`}</Typography>
            <ErrorLine error={error} own={SETUP_ERROR_CODES} testId={`step-${step.key}-upload-error`} />
            <Box>
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={busy ? <CircularProgress size={14} /> : <CloudUploadOutlinedIcon />}
                    disabled={busy}
                    onClick={() => input.current?.click()}
                >
                    {current || step.state === 'done' ? 'Upload another image' : 'Upload an image'}
                </Button>
                <input
                    ref={input}
                    type="file"
                    accept={ACCEPTED_TYPES.join(',')}
                    hidden
                    data-testid={`step-${step.key}-file`}
                    onChange={onFile}
                />
            </Box>
        </Stack>
    );
}
